import { FC, useMemo, useRef, useEffect, useState } from 'react'
import { RetroWindow, PixelText } from '@/components/retro'
import { useGameStore } from '@/store'
import { calculateUnrealizedPnL } from '@/game/trading'

// Track price changes for animations
interface PriceChange {
  symbol: string
  direction: 'up' | 'down'
  timestamp: number
}

export const PortfolioPanel: FC = () => {
  const { session } = useGameStore()
  const [priceChanges, setPriceChanges] = useState<Map<string, PriceChange>>(new Map())
  const prevPricesRef = useRef<Map<string, number>>(new Map())

  // Detect price changes and trigger animations
  useEffect(() => {
    if (!session) return

    const currentPrices = new Map<string, number>()
    session.player.portfolio.holdings.forEach(h => {
      const price = session.market.prices[h.symbol]?.price || h.currentPrice
      currentPrices.set(h.symbol, price)
    })

    const newChanges = new Map<string, PriceChange>()
    currentPrices.forEach((price, symbol) => {
      const prevPrice = prevPricesRef.current.get(symbol)
      if (prevPrice !== undefined && prevPrice !== price) {
        newChanges.set(symbol, {
          symbol,
          direction: price > prevPrice ? 'up' : 'down',
          timestamp: Date.now(),
        })
      }
    })

    if (newChanges.size > 0) {
      setPriceChanges(prev => {
        const updated = new Map(prev)
        newChanges.forEach((change, symbol) => {
          updated.set(symbol, change)
        })
        return updated
      })

      // Clear animations after 1 second
      setTimeout(() => {
        setPriceChanges(prev => {
          const updated = new Map(prev)
          newChanges.forEach((_, symbol) => {
            const change = updated.get(symbol)
            if (change && Date.now() - change.timestamp >= 1000) {
              updated.delete(symbol)
            }
          })
          return updated
        })
      }, 1000)
    }

    prevPricesRef.current = currentPrices
  }, [session?.market.prices])

  if (!session) return null

  const { holdings, totalValue } = session.player.portfolio
  const { market, startingAmount } = session

  // Calculate overall P&L
  const overallPnL = totalValue - startingAmount
  const overallPnLPercent = (overallPnL / startingAmount) * 100

  // Sort holdings by value (USDC first, then by value)
  const sortedHoldings = useMemo(() => {
    return [...holdings].sort((a, b) => {
      if (a.symbol === 'USDC') return -1
      if (b.symbol === 'USDC') return 1
      const aValue = a.amount * (market.prices[a.symbol]?.price || 0)
      const bValue = b.amount * (market.prices[b.symbol]?.price || 0)
      return bValue - aValue
    })
  }, [holdings, market.prices])

  const formatCurrency = (value: number): string => {
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`
    }
    return `$${value.toFixed(2)}`
  }

  return (
    <RetroWindow title="PORTFOLIO" className="h-full">
      <div className="flex flex-col h-full">
        {/* Total Value Header */}
        <div className="p-3 border-b-2 border-game-border bg-game-panel/50">
          <div className="flex justify-between items-center">
            <span className="font-retro text-sm text-game-muted">Total Value</span>
            <div className="text-right">
              <PixelText size="lg" color="accent">
                {formatCurrency(totalValue)}
              </PixelText>
              <div className={`font-mono text-xs ${overallPnL >= 0 ? 'text-game-profit' : 'text-game-loss'}`}>
                {overallPnL >= 0 ? '+' : ''}{formatCurrency(Math.abs(overallPnL))}
                {' '}({overallPnL >= 0 ? '+' : ''}{overallPnLPercent.toFixed(2)}%)
              </div>
            </div>
          </div>
        </div>

        {/* Holdings List */}
        <div className="flex-1 overflow-y-auto">
          {sortedHoldings.map((holding) => {
            const price = market.prices[holding.symbol]?.price || holding.currentPrice
            const value = holding.amount * price
            const { pnl, percentage } = holding.symbol === 'USDC'
              ? { pnl: 0, percentage: 0 }
              : calculateUnrealizedPnL(holding, market)
            const allocation = (value / totalValue) * 100

            // Check for price change animation
            const priceChange = priceChanges.get(holding.symbol)
            const flashClass = priceChange
              ? priceChange.direction === 'up'
                ? 'animate-flash-green'
                : 'animate-flash-red'
              : ''

            return (
              <div
                key={holding.symbol}
                className={`px-3 py-2 border-b border-game-border/50 hover:bg-game-accent/10 transition-colors ${flashClass}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-pixel text-xs text-game-accent">{holding.symbol}</span>
                      {holding.symbol !== 'USDC' && (
                        <span className={`font-mono text-xs px-1 ${
                          percentage >= 0 ? 'bg-game-profit/20 text-game-profit' : 'bg-game-loss/20 text-game-loss'
                        }`}>
                          {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-game-muted mt-0.5">
                      {holding.amount < 1
                        ? holding.amount.toFixed(6)
                        : holding.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })
                      }
                      {holding.symbol !== 'USDC' && (
                        <> @ ${holding.avgBuyPrice < 0.01 ? holding.avgBuyPrice.toFixed(6) : holding.avgBuyPrice.toFixed(2)}</>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-game-text">
                      {formatCurrency(value)}
                    </div>
                    {holding.symbol !== 'USDC' && (
                      <div className={`font-mono text-xs ${pnl >= 0 ? 'text-game-profit' : 'text-game-loss'}`}>
                        {pnl >= 0 ? '+' : ''}{formatCurrency(Math.abs(pnl))}
                      </div>
                    )}
                    <div className="font-mono text-xs text-game-muted">
                      {allocation.toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* Allocation Bar */}
                <div className="mt-1 h-1 bg-game-border/50 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      holding.symbol === 'USDC' ? 'bg-game-gold' : 'bg-game-accent'
                    }`}
                    style={{ width: `${Math.min(allocation, 100)}%` }}
                  />
                </div>
              </div>
            )
          })}

          {holdings.length === 0 && (
            <div className="p-4 text-center">
              <PixelText size="xs" color="muted">No holdings yet</PixelText>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="p-2 border-t-2 border-game-border bg-game-panel/50">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="font-retro text-xs text-game-muted">Positions</div>
              <div className="font-mono text-sm text-game-text">
                {holdings.filter(h => h.symbol !== 'USDC').length}
              </div>
            </div>
            <div>
              <div className="font-retro text-xs text-game-muted">Cash</div>
              <div className="font-mono text-sm text-game-gold">
                {((holdings.find(h => h.symbol === 'USDC')?.amount || 0) / totalValue * 100).toFixed(0)}%
              </div>
            </div>
            <div>
              <div className="font-retro text-xs text-game-muted">At Risk</div>
              <div className="font-mono text-sm text-game-accent">
                {(100 - (holdings.find(h => h.symbol === 'USDC')?.amount || 0) / totalValue * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </RetroWindow>
  )
}
